<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@stores/auth'
import { useAppearanceStore } from '@stores/appearance'
import { useFavoritesStore } from '@stores/favorites'
import { useGenresStore } from '@stores/genres'
import { useMessages } from '@composables/use-messages'
import GenreGridBlock from '@components/genre/genre-grid-block'

const router = useRouter()
const authStore = useAuthStore()
const genresStore = useGenresStore()
const appearanceStore = useAppearanceStore()
const favoritesStore = useFavoritesStore()
const getMessageText = useMessages()

// Guards against a second follow being sent while the first is still in flight.
const isReady = ref(true)

const isLoggedIn = computed(() => authStore.loggedIn)

const followedGenreIds = computed(() => favoritesStore.genreIds)

const activeGenres = computed(() => genresStore.activeGenres)

async function handleToggleFollow(genre) {
    if (!isLoggedIn.value) {
        router.push({ name: 'login' })
        return
    }

    if (!isReady.value || !genre) return

    isReady.value = false

    try {
        const isFollowing = followedGenreIds.value.includes(genre.id)
        const toggleFollow = isFollowing ? favoritesStore.remove : favoritesStore.add
        await toggleFollow({ type: 'genres', id: genre.id })
        appearanceStore.handleNotification({
            message: isFollowing ? 'Genre Unfollowed' : 'Following Genre',
            type: 'success',
        })
    } catch (error) {
        console.error('Error toggling genre follow:', error)
        appearanceStore.handleNotification({
            message: getMessageText('SOMETHING_WENT_WRONG'),
            type: 'warning',
        })
    } finally {
        isReady.value = true
    }
}
</script>

<template>
    <div class="explore-genres">
        <BaseSection padded>
            <GenreGridBlock
                :genres="activeGenres"
                :followed-genre-ids="followedGenreIds"
                @toggle-follow="handleToggleFollow"
            />
        </BaseSection>
    </div>
</template>
